/**
 * Timing and logging against one task.
 *
 * Shared by the row controls and the task drawer so the two cannot drift: the
 * rate resolution, the billable default and the "no project yet" refusal are
 * written once. Both surfaces exist because they answer the same need at
 * different moments — the row when you are scanning a list, the drawer when
 * you have opened the one task you are actually working on.
 */

import { useTimer } from "@/context/TimerContext";
import { useToast } from "@/context/ToastContext";
import { useProjects } from "@/hooks/useData";
import { effectiveRate, useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import { defaultKind, kindFields } from "@/lib/billing";
import type { Item } from "@/lib/types";

/** Duplicated from ItemRow to keep this hook out of that module's import cycle. */
function projectIdOf(item: Item): string | undefined {
  const i = item as unknown as { expand?: { phase?: { expand?: { project?: { id?: string } }; project?: string } } };
  return i?.expand?.phase?.expand?.project?.id ?? i?.expand?.phase?.project;
}

function label(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m ? `${h}h${m}m` : `${h}h`;
}

export interface TaskTime {
  /** This task is the one the running timer is on. */
  timingThis: boolean;
  /** Whether a timer is running at all — starting here would take it over. */
  timerRunning: boolean;
  projectId: string | undefined;
  /** Seconds already logged against this task. */
  loggedSeconds: number;
  toggleTimer: () => Promise<void>;
  logMinutes: (minutes: number) => Promise<void>;
}

export function useTaskTime(item: Item): TaskTime {
  const timer = useTimer();
  const toast = useToast();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: entries = [] } = useTimeEntries();
  const { logEntry } = useTimeMutations();

  const projectId = projectIdOf(item);
  const timingThis = timer.running && timer.meta.itemId === item.id;
  const loggedSeconds = entries
    .filter((e) => e.item === item.id)
    .reduce((n, e) => n + (e.durationSeconds || 0), 0);

  function resolved() {
    const project = projects.find((p) => p.id === projectId);
    const r = effectiveRate(project, clients);
    const kind = defaultKind(project, !!r.clientId);
    return { ...r, kind, ...kindFields(kind, r.rate) };
  }

  async function toggleTimer() {
    if (timingThis) { void timer.stop(); return; }
    if (!projectId) { toast("This task has no project yet — sync first"); return; }
    // A timer running on something else is stopped and logged first: moving
    // to this task means that work is done, not that its time should vanish.
    if (timer.running) await timer.stop();
    timer.start({ projectId, itemId: item.id, note: "", kind: resolved().kind });
  }

  async function logMinutes(minutes: number) {
    if (!projectId) { toast("This task has no project yet — sync first"); return; }
    const end = new Date();
    const start = new Date(end.getTime() - minutes * 60_000);
    const r = resolved();
    try {
      await logEntry.mutateAsync({
        projectId, itemId: item.id,
        start: start.toISOString(), end: end.toISOString(),
        durationSeconds: minutes * 60,
        note: "", billable: r.billable, internal: r.internal, source: "manual",
        rate: r.rate, currency: r.currency, clientId: r.clientId,
      });
      toast(`Logged ${label(minutes)} to “${item.title}”`);
    } catch {
      toast("Could not save that entry");
    }
  }

  return {
    timingThis,
    timerRunning: timer.running,
    projectId,
    loggedSeconds,
    toggleTimer,
    logMinutes,
  };
}
